import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../interfaces';

/**
 * Verification status enum matching backend VerificationStatus
 */
export type VerificationStatus = 'incomplete' | 'pending' | 'approved' | 'rejected' | 'needs_info';

/**
 * A single required (is_required) DRIVER-scope setting key, with the
 * driver's current value and whether it's filled - see
 * VerificationController.getDriverRequiredSettings.
 */
export interface DriverRequiredSetting {
  slug: string;
  label: string;
  groupLabel: string;
  value: string | null;
  isFilled: boolean;
}

/**
 * Verification status history record from backend
 */
export interface VerificationHistoryItem {
  id: number;
  userId: number;
  status: VerificationStatus;
  comment: string | null;
  initiatedBy: number;
  initiatorType: 'driver' | 'company';
  assignedTo: number | null;
  ticketId: number | null;
  createdAt: string;
  initiator?: {
    id: number;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  assignee?: {
    id: number;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

/**
 * Unverified driver list item
 */
export interface UnverifiedDriver {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  isActive: boolean;
  isVerified: boolean;
  createdAt: string;
  latestStatus?: VerificationStatus;
  latestComment?: string;
  assignedTo?: number;
}

/**
 * Verify driver request body
 */
export interface VerifyDriverRequest {
  status: 'approved' | 'rejected' | 'needs_info';
  comment?: string;
}

export interface DriverCompanyRegistrationRequest {
  driver: {
    id: number;
    companyId: number | null;
    firstName?: string;
    lastName?: string;
    phone?: string;
    email?: string;
  };
  registrationRequest: {
    companyName: string | null;
    companyRegistrationNumber: string | null;
    companyPhone: string | null;
    companyEmail: string | null;
  };
  foundCompany: {
    id: number;
    name: string;
    type: string;
    parentId: number | null;
    phone: string | null;
    email: string | null;
    driversCount: number;
  } | null;
  contacts: {
    contactsProvided: boolean;
    phoneMatchesDriver: boolean;
    emailMatchesDriver: boolean;
    contactsMatchDriver: boolean;
  };
  actions: {
    canAttachToExistingCompany: boolean;
    canCreateCompany: boolean;
    driverWillBecomeAdmin: boolean;
  };
  network: {
    mainCompanyId: number;
    mainCompanyName: string;
  };
}

/**
 * VerificationService
 *
 * Service for managing driver verification in the company panel.
 * Uses /company/verification endpoints from the backend.
 */
@Injectable({
  providedIn: 'root'
})
export class VerificationService {
  private readonly apiUrl = `${environment.apiUrl}/company/verification`;

  constructor(private http: HttpClient) {}

  /**
   * Get list of unverified drivers
   */
  getUnverifiedDrivers(
    page: number = 1,
    limit: number = 50
  ): Observable<ApiResponse<UnverifiedDriver[]>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    return this.http.get<ApiResponse<UnverifiedDriver[]>>(
      `${this.apiUrl}/drivers/unverified`,
      { params }
    );
  }

  /**
   * Get list of pending verification requests
   */
  getPendingDrivers(
    page: number = 1,
    limit: number = 50
  ): Observable<ApiResponse<UnverifiedDriver[]>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    return this.http.get<ApiResponse<UnverifiedDriver[]>>(
      `${this.apiUrl}/drivers/pending`,
      { params }
    );
  }

  /**
   * Get verification history for a specific driver
   */
  getVerificationHistory(
    userId: number
  ): Observable<ApiResponse<VerificationHistoryItem[]>> {
    return this.http.get<ApiResponse<VerificationHistoryItem[]>>(
      `${this.apiUrl}/drivers/${userId}/history`
    );
  }

  /**
   * Get the driver's required-settings status for verification review
   * (company name/registration number, license/insurance expiry, payout
   * IBAN, etc. - every setting key currently flagged is_required for the
   * DRIVER scope). Previously the reviewer had no way at all to see this
   * before approving/rejecting - the driver app itself only surfaced it as
   * an error message after a failed submit.
   */
  getDriverRequiredSettings(
    userId: number
  ): Observable<ApiResponse<DriverRequiredSetting[]>> {
    return this.http.get<ApiResponse<DriverRequiredSetting[]>>(
      `${this.apiUrl}/drivers/${userId}/settings`
    );
  }

  /**
   * Get driver documents for verification review
   */
  getDriverDocuments(userId: number): Observable<ApiResponse<unknown[]>> {
    return this.http.get<ApiResponse<unknown[]>>(
      `${this.apiUrl}/drivers/${userId}/documents`
    );
  }

  /**
   * Get driver photos for verification review
   */
  getDriverPhotos(userId: number): Observable<ApiResponse<unknown[]>> {
    return this.http.get<ApiResponse<unknown[]>>(
      `${this.apiUrl}/drivers/${userId}/photos`
    );
  }

  /**
   * Approve driver verification (sets isVerified=true)
   */
  approveDriver(
    userId: number,
    comment?: string
  ): Observable<ApiResponse<VerificationHistoryItem>> {
    return this.http.post<ApiResponse<VerificationHistoryItem>>(
      `${this.apiUrl}/drivers/${userId}/approve`,
      { comment }
    );
  }

  /**
   * Reject driver verification (sets isVerified=false)
   */
  rejectDriver(
    userId: number,
    comment: string
  ): Observable<ApiResponse<VerificationHistoryItem>> {
    return this.http.post<ApiResponse<VerificationHistoryItem>>(
      `${this.apiUrl}/drivers/${userId}/reject`,
      { comment }
    );
  }

  /**
   * Request additional info from driver (sets isVerified=false)
   */
  requestInfo(
    userId: number,
    comment: string
  ): Observable<ApiResponse<VerificationHistoryItem>> {
    return this.http.post<ApiResponse<VerificationHistoryItem>>(
      `${this.apiUrl}/drivers/${userId}/needs-info`,
      { comment }
    );
  }

  /**
   * Assign verification to current company user
   */
  assignToSelf(
    verificationId: number
  ): Observable<ApiResponse<VerificationHistoryItem>> {
    return this.http.post<ApiResponse<VerificationHistoryItem>>(
      `${this.apiUrl}/${verificationId}/assign`,
      {}
    );
  }

  getDriverCompanyRequest(
    userId: number
  ): Observable<ApiResponse<DriverCompanyRegistrationRequest>> {
    return this.http.get<ApiResponse<DriverCompanyRegistrationRequest>>(
      `${this.apiUrl}/drivers/${userId}/company-request`
    );
  }

  attachDriverToCompany(
    userId: number
  ): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(
      `${this.apiUrl}/drivers/${userId}/company/attach`,
      {}
    );
  }

  createCompanyForDriver(
    userId: number
  ): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(
      `${this.apiUrl}/drivers/${userId}/company/create`,
      {}
    );
  }
}
